import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import useSWR from 'swr';

import { Icon, Link, Spinner } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Form } from 'components/form';
import { Col, Row } from 'components/layout';

import useDebounce from 'hooks/useDebounce';
import { useNotification } from 'hooks/useNotification';
import { API_ROUTES } from 'lib/api-routes';
import { signUpRecruiterState } from 'lib/atoms/signUpRecruiterAtom';
import { useApp } from 'lib/contexts/save-in-app-context';
import { CompaniesResponse, CompanyAttributes } from 'lib/models/company';
import { PAGE_ROUTES } from 'lib/page-routes';
import { apiInstance } from 'lib/utils/axios';
import { get } from 'lib/utils/http';
import { parseArrayResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';
import { PRIVACY_POLICY, TERMS_OF_USE } from 'utils/urls';

import StepHeading from '../Steps/StepHeading';
import { CompanyData } from '../index.page';
import styles from './Company.module.scss';

export interface CompanyProps {
  className: string;
  setStep: (step: number) => void;
}

export const Company: React.FC<CompanyProps> = ({
  className,
  setStep,
}: CompanyProps) => {
  const router = useRouter();
  const notificationInstance = useNotification();
  const [recruiterState, setRecruiterState] =
    useRecoilState(signUpRecruiterState);
  const { company } = recruiterState;

  const { recruiterMessage } = useApp();

  const [t] = useTranslation('signup.recruiter');

  const contentClassNames = cn([styles.container], className);
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false);
  const [companyNotOnList, setCompanyNotOnList] = React.useState<boolean>(
    !!company?.website
  );

  const { referrer: invitation_code } = router.query;
  const [message, setMessage] = useState('');

  useEffect(() => {
    (async () => {
      if (invitation_code) {
        try {
          const response = await apiInstance.post(
            'api/v1/recruiter/invitation_details',
            {
              invitation_code,
            }
          );
          const message = response.data.data;
          setMessage(message);
        } catch (error) {
          // @ts-ignore
          const message = error.response.data.data;
          notificationInstance.error({
            title: 'Error',
            message,
          });
        }
      }
    })();
  }, [invitation_code]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting, isDirty },
    watch,
    setFocus,
  } = useForm({
    defaultValues: {
      name: company?.name || '',
      id: company?.website ? '' : company?.id,
      website: company?.website,
    },
  });

  const name = watch('name');
  const id = watch('id');

  useEffect(() => {
    if (!name) setFocus('name');
  }, [setFocus]);

  const debouncedSearch = useDebounce(name.trim(), 250);
  const { data, isLoading } = useSWR<CompaniesResponse>(
    `${API_ROUTES.COMPANIES}?name=${debouncedSearch}&per_page=100&with_icon=true`,
    get
  );

  const companies = useMemo(() => {
    const list = parseArrayResponse<CompanyAttributes>(data);
    return list.map(({ id, name, icon_url }) => ({
      id,
      value: id,
      label: name,
      avatar: icon_url,
    }));
  }, [data]);

  const onSubmit: SubmitHandler<CompanyData> = async (input) => {
    const data = { ...input, isNewCompany: input.id ? false : true };
    setRecruiterState({ ...recruiterState, company: data, step: 1 });
    setStep(1);
  };

  useEffect(() => {
    if (isDirty) {
      setShowDropDown(true);
    }
  }, [isDirty]);

  return (
    <section className={contentClassNames}>
      <StepHeading
        title={t('company.header.title')}
        desc={t('company.header.desc')}
      />
      <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Row className={styles.row}>
          <Col span={24} className={styles.col}>
            <input type="hidden" {...register('id')} />
            <InputV2
              type="text"
              id="company-name"
              label={companyNotOnList ? t('company.company.label') : ''}
              placeholder={t('company.company.placeholder')}
              {...register('name', {
                required: t('company.company.required'),
              })}
              isInvalid={!!errors.name}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
                setShowDropDown(true);
              }}
            />
            {showDropDown && name && (
              <div className={styles.dropdownList}>
                {(isLoading || companies.length > 0) && (
                  <div className={styles.dropdownListWrapper}>
                    {isLoading && (
                      <div className={styles.dropdownListItem}>
                        <Spinner size="small" />
                      </div>
                    )}
                    {companies?.map((option, index) => (
                      <div
                        key={index}
                        className={styles.dropdownListItem}
                        onClick={() => {
                          setValue('id', option.id);
                          setValue('name', option.label);
                          setValue('website', '');
                          setShowDropDown(false);
                          setCompanyNotOnList(false);
                          handleSubmit(onSubmit)();
                        }}
                      >
                        <img
                          src={option.avatar}
                          alt={option.label}
                          className={styles.dropdownListItemAvatar}
                        />
                        <div
                          className={styles.dropdownListItemText}
                          key={option.id}
                        >
                          {option.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  className={cn(styles.dropdownListWrapper, {
                    [styles.dropdownListWrapperBorder]:
                      isLoading || !!companies?.length,
                  })}
                >
                  <div className={styles.dropdownListItem}>
                    <div
                      className={cn(
                        styles.dropdownListItemText,
                        styles.dropdownListItemTextNotOnList
                      )}
                      onClick={() => {
                        setValue('id', '');
                        setShowDropDown(false);
                        setCompanyNotOnList(true);
                      }}
                    >
                      {t('company.company.not-on-list')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Col>
          {companyNotOnList && (
            <Col span={24} className={styles.marginTop}>
              <InputV2
                type="text"
                label={t('company.website.label')}
                {...register('website', {
                  required: t('company.website.required'),
                  pattern: {
                    value: REGEX.URL,
                    message: t('company.website.invalid'),
                  },
                })}
                isInvalid={!!errors.website}
                labelClassName={styles.fieldLabel}
                placeholder={t('company.website.placeholder')}
              />
              {errors.website?.message && (
                <div className={styles.errorMessage}>
                  {errors.website?.message}
                </div>
              )}
            </Col>
          )}
          {message ? (
            <Row columnGap={8} wrap={false} className={styles.messageRow}>
              <Icon iconName="icon_info" className={styles.messageIcon} />
              <p className={styles.message}>{message}</p>
            </Row>
          ) : null}
          {recruiterMessage ? (
            <Row columnGap={8} wrap={false} className={styles.messageRow}>
              <Icon iconName="icon_info" className={styles.messageIcon} />
              <p className={styles.message}>{t('company.message')}</p>
            </Row>
          ) : null}
        </Row>

        {companyNotOnList ? (
          <ButtonV3
            type="submit"
            className={styles.submitButton}
            isFullWidth
            isLoading={isSubmitting}
            size="xlarge"
            disabled={name ? !(id || companyNotOnList) : false}
          >
            {t('company.continue')}
          </ButtonV3>
        ) : null}
      </Form>

      <p className={styles.signupLink}>
        {t('company.already-have-account')}
        <Link href={PAGE_ROUTES.SIGN_IN_RECRUITER} isPrimary>
          {t('company.sign-in')}
        </Link>
      </p>

      <p className={styles.termsAndPrivacy}>
        {t('company.agree-text')}
        <br />
        <Link href={TERMS_OF_USE}>{t('company.terms-of-service')}</Link>
        {t('company.and-our')}
        <Link href={PRIVACY_POLICY}>{t('company.privacy-policy')}</Link>
      </p>
    </section>
  );
};
